import { ReactElement, useState } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	ConfirmationDialog,
	FormattedText,
	Icon,
	Space,
	Spinner,
	TransparentButtonList,
	Typography
} from "client/Components";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ListLocationModel } from "client/Api/Documents/SharePoint/common";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { createUrlRelativeToEnvironment, navigateTo } from "client_legacy/ndaw/navigator";
import { cancelJob } from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	CrossStoreNotificationDataViewModel,
	CrossStoreNotificationKind,
	CrossStoreMoveInProgressNotificationDataViewModel,
	CrossStoreCopyInProgressNotificationDataViewModel,
	CrossStoreMoveFinishedNotificationDataViewModel,
	CrossStoreCopyFinishedNotificationDataViewModel,
	OperationType,
	CrossStoreFinishStatus,
	CrossStoreMoveCancellationInProgressNotificationDataViewModel,
	CrossStoreCopyCancellationInProgressNotificationDataViewModel,
	CrossStoreDestination,
	SharePointDestination,
	WebDavDestination,
	CrossStoreFinishedNotificationDataViewModel
} from "client/Api/Notifications/notifications";

export type CrossStoreActivityProps = {
	activityId: number;
	data: CrossStoreNotificationDataViewModel;
	onActivityChanged: () => void;
};

export const CrossStoreActivity = (props: CrossStoreActivityProps): ReactElement => {
	const {
		activityId,
		data,
		onActivityChanged
	} = props;

	switch (data.kind) {
		case CrossStoreNotificationKind.MoveInProgress:
			return (
				<InProgressActivity
					activityId={activityId}
					data={data}
					onActivityChanged={onActivityChanged}
				/>
			);
		case CrossStoreNotificationKind.MoveFinished:
			return <FinishedActivity data={data}/>;
		case CrossStoreNotificationKind.CopyInProgress:
			return (
				<InProgressActivity
					activityId={activityId}
					data={data}
					onActivityChanged={onActivityChanged}
				/>
			);
		case CrossStoreNotificationKind.CopyFinished:
			return <FinishedActivity data={data}/>;
		case CrossStoreNotificationKind.MoveCancellationInProgress:
			return <CancellationInProgressActivity data={data} />;
		case CrossStoreNotificationKind.CopyCancellationInProgress:
			return <CancellationInProgressActivity data={data} />;
		default:
			throw new UnreachableCaseError(data);
	}
};

CrossStoreActivity.displayName = "CrossStoreActivity";

type CancellationConfirmationDialogState = {
	isVisible: false;
} | {
	isVisible: true;
	title: string;
	message: string;
};

type InProgressActivityProps = {
	activityId: number;
	onActivityChanged: () => void;
	data: CrossStoreMoveInProgressNotificationDataViewModel | CrossStoreCopyInProgressNotificationDataViewModel;
};

const InProgressActivity = (props: InProgressActivityProps): ReactElement => {
	const {
		data,
		onActivityChanged
	} = props;

	const {
		kind,
		itemsCount,
		masterJobId,
		destinationName
	} = data;

	const [cancellationConfirmationDialog, setCancellationConfirmationDialog] = useState<CancellationConfirmationDialogState>({
		isVisible: false
	});
	const alertManager: AlertManager = useAlerts();

	const cancelHandler = (): void => {
		switch (kind) {
			case CrossStoreNotificationKind.MoveInProgress:
				setCancellationConfirmationDialog({
					isVisible: true,
					title: localize("_AreYouSureYouWantToCancelTheMovingOfTheseItems"),
					message: localize("_ItMayTakeSomeTimeBeforeTheCancellationIsDone")
				});
				break;
			case CrossStoreNotificationKind.CopyInProgress:
				setCancellationConfirmationDialog({
					isVisible: true,
					title: localize("_AreYouSureYouWantToCancelTheCopyingOfTheseItems"),
					message: localize("_ItMayTakeSomeTimeBeforeTheCancellationIsDone")
				});
				break;
			default:
				throw new UnreachableCaseError(kind);
		}
	};

	const cancelConfirmHandler = async (): Promise<void> => {
		setCancellationConfirmationDialog({isVisible: false});
		try {
			await cancelJob(masterJobId);
		} catch(e) {
			alertManager.showError(localize("_GeneralErrorMessage"));
			console.error(`Unable to cancel job ${masterJobId}`, e);
		}
		onActivityChanged();
	};

	let text: ReactElement;

	switch (data.kind) {
		case CrossStoreNotificationKind.MoveInProgress:
			text = (
				<FormattedText
					text={localize("_MovingAmountItemsTo")}
					thingsToReplace={
						[
							{
								what: "{{Amount}}",
								filler: (
									<Typography weight="bold">
										{itemsCount}
									</Typography>
								)
							}
						]
					}
				/>
			);
			break;
		case CrossStoreNotificationKind.CopyInProgress:
			text = (
				<FormattedText
					text={localize("_CopyingAmountItemsTo")}
					thingsToReplace={
						[
							{
								what: "{{Amount}}",
								filler: (
									<Typography weight="bold">
										{itemsCount}
									</Typography>
								)
							}
						]
					}
				/>
			);
			break;
		default:
			throw new UnreachableCaseError(data);
	}

	return (
		<>
			<Spinner isInline />
			<Space />
			{text}
			<Space />
			<Typography weight="bold">
				{destinationName}
			</Typography>
			<Space />
			<TransparentButtonList responsiveBehavior="wrap">
				<TransparentButtonList.Item
					icon="xmark"
					text={localize("_Cancel")}
					onClick={cancelHandler}
					bubbleClick={false}
				/>
			</TransparentButtonList>
			{cancellationConfirmationDialog.isVisible && (
				<ConfirmationDialog
					title={cancellationConfirmationDialog.title}
					confirmText={localize("_Yes")}
					cancelText={localize("_No")}
					onCancellation={() => setCancellationConfirmationDialog({isVisible: false})}
					onConfirmation={cancelConfirmHandler}
					trackingId="cancel-cross-store-operation-confirmation-dialog"
				>
					{cancellationConfirmationDialog.message}
				</ConfirmationDialog>
			)}
		</>
	);
};

InProgressActivity.displayName = "InProgressActivity";

type FinishedActivityProps = {
	data: CrossStoreMoveFinishedNotificationDataViewModel | CrossStoreCopyFinishedNotificationDataViewModel;
};

const FinishedActivity = (props: FinishedActivityProps): ReactElement => {
	const {
		data
	} = props;

	const {
		status,
		itemsCount,
		destinationName,
		kind
	} = data;

	const operationType: OperationType = crossStoreNotificationKindToOperationTypeMap[kind];

	const navigationUrl: string = createNavigationUrl(data.destination);

	switch (status) {
		case CrossStoreFinishStatus.SuccessfullyFinished:
			return (
				<>
					<Typography weight="bold">
						{itemsCount}
					</Typography>
					<Space />
					{localize(finishedSuccessfullyTextKey[operationType])}
					<Space />
					<Typography weight="bold">
						{destinationName}
					</Typography>.
				</>
			);
		case CrossStoreFinishStatus.FinishedWithErrors:
			return (
				<>
					<Typography weight="bold">
						{itemsCount}
					</Typography>
					<Space />
					{localize(finishedSuccessfullyTextKey[operationType])}
					<Space />
					<Typography weight="bold">
						{destinationName}
					</Typography>.
					<br />
					<Icon
						value={{
							kind: "font",
							name: "circle-xmark",
							color: "danger"
						}}
						size="small"
					/>
					<Space />
					{localize("_SomeErrorsOccured")}.
					<Space />
					<TransparentButtonList responsiveBehavior="wrap">
						<TransparentButtonList.Item
							icon="folder-open"
							bubbleClick={false}
							text={localize("_OpenLocation")}
							onClick={()=> navigateTo(navigationUrl)}
						/>
					</TransparentButtonList>
				</>
			);
		case CrossStoreFinishStatus.Failed:
			return (
				<>
					<Icon
						value={{
							kind: "font",
							name: "circle-xmark",
							color:"danger"
						}}
						size="small"
					/>
					<Space />
					{localize(finishedWithFailureTextKey[operationType])}
					<Space />
					<Typography weight="bold">
						{itemsCount}
					</Typography>
					<Space />
					{localize("_ItemsTo")}
					<Space />
					<Typography weight="bold">
						{destinationName}
					</Typography>.
				</>
			);
		case CrossStoreFinishStatus.Cancelled:
			return (
				<>
					<FormattedText
						text={localize(cancelledTextKey[operationType])}
						thingsToReplace={
							[
								{
									what: "{{Amount}}",
									filler: (
										<Typography weight="bold">
											{itemsCount}
										</Typography>
			 						)
								}
							]
						}
					/>
					<Space />
					<Typography weight="bold">
						{destinationName}
					</Typography>.
				</>
			);
		default:
			throw new UnreachableCaseError(status);
	}
};

FinishedActivity.displayName = "FinishedActivity";

type CancellationInProgressActivityProps = {
	data: CrossStoreMoveCancellationInProgressNotificationDataViewModel | CrossStoreCopyCancellationInProgressNotificationDataViewModel;
};

const CancellationInProgressActivity = (props: CancellationInProgressActivityProps): ReactElement => {
	const {
		data
	} = props;

	let text: ReactElement;

	switch (data.kind) {
		case CrossStoreNotificationKind.MoveCancellationInProgress:
			text = (
				<FormattedText
					text={localize("_CancellingMovingAmountItemsTo")}
					thingsToReplace={
						[
							{
								what: "{{Amount}}",
								filler: (
									<Typography weight="bold">
										{data.itemsCount}
									</Typography>
			 					)
							}
						]
					}
				/>
			);
			break;
		case CrossStoreNotificationKind.CopyCancellationInProgress:
			text = (
				<FormattedText
					text={localize("_CancellingCopyingAmountItemsTo")}
					thingsToReplace={
						[
							{
								what: "{{Amount}}",
								filler: (
									<Typography weight="bold">
										{data.itemsCount}
									</Typography>
								)
							}
						]
					}
				/>
			);
			break;
		default:
			throw new UnreachableCaseError(data);
	}

	return (
		<>
			<Spinner isInline />
			<Space />
			{text}
			<Space />
			<Typography weight="bold">
				{data.destinationName}
			</Typography>
		</>
	);
};

CancellationInProgressActivity.displayName = "CancellationInProgressActivity";

export const crossStoreNotificationKindToOperationTypeMap: {
	[key in CrossStoreNotificationKind]: OperationType;
} = {
	[CrossStoreNotificationKind.CopyInProgress]: OperationType.Copy,
	[CrossStoreNotificationKind.CopyCancellationInProgress]: OperationType.Copy,
	[CrossStoreNotificationKind.CopyFinished]: OperationType.Copy,
	[CrossStoreNotificationKind.MoveInProgress]: OperationType.Move,
	[CrossStoreNotificationKind.MoveCancellationInProgress]: OperationType.Move,
	[CrossStoreNotificationKind.MoveFinished]: OperationType.Move,
};

const finishedSuccessfullyTextKey: { [key in OperationType]: LocalizationKey } = {
	[OperationType.Move]: "_ItemsMovedTo",
	[OperationType.Copy]: "_ItemsCopiedTo"
};

const finishedWithFailureTextKey: { [key in OperationType]: LocalizationKey } = {
	[OperationType.Move]: "_FailedToMove",
	[OperationType.Copy]: "_FailedToCopy"
};

const cancelledTextKey: { [key in OperationType]: LocalizationKey } = {
	[OperationType.Move]: "_CancelledMovingAmountItemsTo",
	[OperationType.Copy]: "_CancelledCopyingAmountItemsTo"
};

export function createNavigationUrl(destination: CrossStoreDestination): string {
	switch (destination.storeType) {
		case DocumentStoreType.SharePoint:
			return createNavigationUrlForSharePoint(destination);
		case DocumentStoreType.WebDav:
			return createNavigationUrlForWebDav(destination);
		default:
			throw new UnreachableCaseError(destination);
	}
}

function createNavigationUrlForSharePoint(destination: SharePointDestination): string {
	const location: ListLocationModel = {
		siteUrl: destination.spSiteUrl,
		listId: destination.listId
	};

	const queryString: string = urlEncodeObject({
		location,
		folderId: destination.folderId
	});

	return createUrlRelativeToEnvironment("/Documents/SharePoint/RedirectToFolder?" + queryString);
}

function createNavigationUrlForWebDav(destination: WebDavDestination): string {
	const queryString: string = urlEncodeObject({
		serverUrl: destination.serverUrl,
		folderId: destination.folderId
	});
	return createUrlRelativeToEnvironment(`/Documents/WebDav/RedirectToFolder/?` + queryString);
}

export function hadErrors(data: CrossStoreNotificationDataViewModel): boolean {
	switch (data.kind) {
		case CrossStoreNotificationKind.MoveFinished:
		case CrossStoreNotificationKind.CopyFinished:
			return data.status !== CrossStoreFinishStatus.SuccessfullyFinished && data.status !== CrossStoreFinishStatus.Cancelled;

		default:
			return false;
	}
}

export function isCrossStoreFinishedNotificationDataViewModel(
	model: CrossStoreNotificationDataViewModel
): model is CrossStoreFinishedNotificationDataViewModel {
	if (
		model.kind === CrossStoreNotificationKind.MoveFinished
		|| model.kind === CrossStoreNotificationKind.CopyFinished
	) {
		return true;
	}
	return false;
}